import type { MeetingDetail, MeetingInput } from '@/lib/apiClient';
import type { BadgeTone } from '@/components';
import { toEasternInputValue } from '../lib/sentAt';

/** A meeting's status, as a badge. */
export const STATUS_TONE: Record<string, BadgeTone> = { planned: 'info', held: 'success', cancelled: 'neutral' };

/**
 * The meeting editor's working copy (#644), and the body it saves. Kept apart
 * from the component so the rules can be read, and tested, in one place.
 *
 * A meeting is saved whole: its details, who is invited (and who came), and its
 * agenda items with the minutes on each. An item that came from the server keeps
 * its entryId, so the save updates it in place; one added here has none.
 *
 * ⚠️ The start is EASTERN wall-clock, whatever the browser's zone, as the API
 * reads it (see lib/sentAt): a PM on a laptop set to Central must see and save
 * the office's 10:00 AM.
 */

export interface AttendeeDraft {
  /** A stable key for React while the list is edited; never sent. */
  key: string;
  contactId: number | null;
  name: string;
  company: string;
  /** '' when not recorded. */
  attendance: '' | 'present' | 'absent';
}

export interface ItemDraft {
  key: string;
  entryId?: number;
  title: string;
  section: string;
  discussion: string;
  status: string;
  responsible: string;
  /** YYYY-MM-DD, or ''. */
  dueDate: string;
}

export interface MeetingDraft {
  meetingType: string;
  title: string;
  /** YYYY-MM-DDTHH:mm, Eastern. */
  startsAt: string;
  location: string;
  status: string;
  attendees: AttendeeDraft[];
  items: ItemDraft[];
}

let seq = 0;
const nextKey = () => `k${++seq}`;

export function blankItem(section = ''): ItemDraft {
  return { key: nextKey(), title: '', section, discussion: '', status: 'open', responsible: '', dueDate: '' };
}

export function attendee(name: string, company: string | null, contactId: number | null = null): AttendeeDraft {
  return { key: nextKey(), contactId, name, company: company ?? '', attendance: '' };
}

/** A new meeting: an OAC meeting unless said otherwise, with no time set yet and one empty item to start the agenda. */
export function newDraft(meetingType = 'oac'): MeetingDraft {
  return { meetingType, title: '', startsAt: '', location: '', status: 'planned', attendees: [], items: [blankItem()] };
}

/** A saved meeting as the editor holds it. */
export function draftFrom(m: MeetingDetail): MeetingDraft {
  return {
    meetingType: m.meetingType,
    title: m.title ?? '',
    startsAt: toEasternInputValue(m.startsAt),
    location: m.location ?? '',
    status: m.status,
    attendees: m.attendees.map((a) => ({
      key: nextKey(), contactId: a.contactId, name: a.name, company: a.company ?? '',
      attendance: a.attendance === 'present' || a.attendance === 'absent' ? a.attendance : '',
    })),
    items: m.items.map((i) => ({
      key: nextKey(), entryId: i.entryId, title: i.title, section: i.section ?? '', discussion: i.discussion ?? '',
      status: i.status, responsible: i.responsible ?? '', dueDate: i.dueDate ?? '',
    })),
  };
}

const orNull = (s: string) => (s.trim() ? s.trim() : null);

/**
 * The body to save. An item left with no title at all — the empty row a new
 * agenda starts with — is dropped rather than refused; one with minutes or a
 * responsible party but no title is a mistake, and `problems` says so.
 */
export function toInput(d: MeetingDraft): MeetingInput {
  return {
    title: orNull(d.title),
    startsAt: d.startsAt,
    location: orNull(d.location),
    status: d.status,
    attendees: d.attendees.map((a) => ({
      contactId: a.contactId, name: a.name.trim(), company: orNull(a.company), attendance: a.attendance || null,
    })),
    items: d.items.filter((i) => !isEmptyItem(i)).map((i) => ({
      ...(i.entryId != null ? { entryId: i.entryId } : {}),
      title: i.title.trim(),
      section: orNull(i.section),
      discussion: orNull(i.discussion),
      status: i.status,
      responsible: orNull(i.responsible),
      dueDate: i.dueDate || null,
    })),
  };
}

/** Nothing typed into it: the blank row a new agenda starts with, or one added and never used. */
export function isEmptyItem(i: ItemDraft): boolean {
  return i.entryId == null && !i.title.trim() && !i.section.trim() && !i.discussion.trim() && !i.responsible.trim() && !i.dueDate;
}

/** What stops a save, in words, or [] when it can go. The server checks all of this too. */
export function problems(d: MeetingDraft): string[] {
  const out: string[] = [];
  if (!d.startsAt) out.push('Set when the meeting starts.');
  if (d.attendees.some((a) => !a.name.trim())) out.push('Every attendee needs a name.');
  const untitled = d.items.filter((i) => !isEmptyItem(i) && !i.title.trim()).length;
  if (untitled) out.push(untitled === 1 ? 'An agenda item has no title.' : `${untitled} agenda items have no title.`);
  return out;
}

/** Move an item up (-1) or down (+1) the agenda. */
export function moveItem(items: ItemDraft[], key: string, by: -1 | 1): ItemDraft[] {
  const from = items.findIndex((i) => i.key === key);
  const to = from + by;
  if (from < 0 || to < 0 || to >= items.length) return items;
  const next = items.slice();
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}
